import React, { useState, useEffect } from "react";
import {
  Button,
  IconButton,
  Box,
  Autocomplete,
  Popover,
  TextField,
  Typography,
  Grid,
  Container,
  Radio,
  RadioGroup,
  FormControlLabel,
} from "@mui/material";
import { SwapHoriz, People, Remove, Add, Search } from "@mui/icons-material";
import { ErrorOutline } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { searchCities, searchTrips, searchPartnerTrips, getTopCities } from "../api";
import { getCityDisplay, getCityKey } from "../utils/city";
import { useMediaQuery } from "@mui/material";
import { useBrandColors, useBrand } from "../brand/BrandContext";

const MAX_PASSENGERS = 9;

function cityFieldFontSize(value) {
  const length = String(value || "").trim().length;
  if (length > 14) return "14px";
  if (length > 10) return "15px";
  return "16px";
}

function passengersFromSearch(params) {
  const rawAdults = Number(params.get("adults"));
  const rawChildren = Number(params.get("children"));
  const adults = Number.isInteger(rawAdults) && rawAdults >= 1 ? rawAdults : 1;
  const children = Number.isInteger(rawChildren) && rawChildren >= 0 ? rawChildren : 0;
  if (adults + children > MAX_PASSENGERS) {
    return { adults: 1, children: 0 };
  }
  return { adults, children };
}

const SearchBar = ({ 
  setTrips, 
  setShowResults, 
  setLoadingTrips,
  setLoadingPartners,
  loadingTrips,

  setSearchedFromCity, 
  setSearchedToCity, 
  setSearchedDate,
  setSearchedAdults,
  setSearchedChildren,
  setSearchedReturnDate,

  boxWidth,
  showHeadText,
  handleSearch
}) => {
  const { t, i18n } = useTranslation("common");
  const BRAND = useBrandColors();
  const brand = useBrand();
  const partnerSalesEnabled = brand.features?.partner_sales === true;
  const lang = (i18n.language && i18n.language.startsWith("en")) ? "en" : "uk";
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [fromCity, setFromCity] = useState(searchParams.get("from") || "");
  const [toCity, setToCity] = useState(searchParams.get("to") || "");
  const [date, setDate] = useState(searchParams.get("date") ? dayjs(searchParams.get("date")) : dayjs());
  const [returnDate, setReturnDate] = useState(
    searchParams.get("return") ? dayjs(searchParams.get("return")) : null
  );
  const [tripType, setTripType] = useState(
    searchParams.get("return") ? "round_trip" : "one_way"
  );
  const initialPassengers = passengersFromSearch(searchParams);
  const [adults, setAdults] = useState(initialPassengers.adults);
  const [children, setChildren] = useState(initialPassengers.children);

  const [fromError, setFromError] = useState(false);
  const [toError, setToError] = useState(false);
  const [dateError, setDateError] = useState(false);


  const [topFromCities, setTopFromCities] = useState([]);
  const [topToCities, setTopToCities] = useState([]);
  const [fromCityOptions, setFromCityOptions] = useState([]);
  const [toCityOptions, setToCityOptions] = useState([]);
  const [loadingFrom, setLoadingFrom] = useState(false);
  const [loadingTo, setLoadingTo] = useState(false);
  const [passengerAnchor, setPassengerAnchor] = useState(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      getTopCities(lang, 5, "from"),
      getTopCities(lang, 5, "to"),
    ])
      .then(([fromData, toData]) => {
        if (cancelled) return;
        const fromList = Array.isArray(fromData) ? fromData : [];
        const toList = Array.isArray(toData) ? toData : [];
        setTopFromCities(fromList);
        setTopToCities(toList);
        setFromCityOptions(fromList);
        setToCityOptions(toList);
      })
      .catch(() => {
        if (cancelled) return;
        setTopFromCities([]);
        setTopToCities([]);
        setFromCityOptions([]);
        setToCityOptions([]);
      });
    return () => { cancelled = true; };
  }, [lang]);

  const fetchCities = async (input, setOptions, setLoading, topList) => {
    if (input.length < 2) {
      setOptions(topList);
      return;
    }
    setLoading(true);
    try {
      const response = await searchCities(input, lang);
      setOptions(Array.isArray(response) ? response : []);
    } catch (error) {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const newFrom = searchParams.get("from") || "";
    const newTo = searchParams.get("to") || "";
    const newDate = searchParams.get("date") ? dayjs(searchParams.get("date")) : dayjs();
    const newReturn = searchParams.get("return")
      ? dayjs(searchParams.get("return"))
      : null;
    const { adults: newAdults, children: newChildren } = passengersFromSearch(searchParams);
    const focusField = searchParams.get("focus");

    if (
      newFrom &&
      newTo &&
      newDate &&
      !isNaN(newAdults) &&
      !isNaN(newChildren)
    ) {
      setFromCity(newFrom);
      setToCity(newTo);
      setDate(newDate);
      setReturnDate(newReturn);
      setTripType(newReturn ? "round_trip" : "one_way");
      setAdults(newAdults);
      setChildren(newChildren);

      defaultHandleSearch(newFrom, newTo, newDate, newAdults, newChildren, newReturn);
      return;
    }

    if (newFrom || newTo) {
      setFromCity(newFrom);
      setToCity(newTo);
      setDate(newDate);
      setReturnDate(newReturn);
      setTripType(newReturn ? "round_trip" : "one_way");
      setAdults(newAdults);
      setChildren(newChildren);
      setShowResults(false);
      setSearchedReturnDate?.(null);

      const field =
        focusField === "to" || focusField === "from"
          ? focusField
          : newFrom
            ? "to"
            : "from";
      window.setTimeout(() => {
        const el = document.querySelector(`[data-search-field="${field}"]`);
        if (el && typeof el.focus === "function") el.focus();
      }, 400);
      return;
    }

    if (!newFrom && !newTo) {
      setFromCity("");
      setToCity("");
      setDate(dayjs());
      setReturnDate(null);
      setTripType("one_way");
      setAdults(1);
      setChildren(0);
      setShowResults(false);
      setSearchedReturnDate?.(null);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    const hasAllParams =
      fromCity &&
      toCity &&
      date &&
      !isNaN(adults) &&
      !isNaN(children);

    if (hasAllParams) {
      defaultHandleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  const defaultHandleSearch = async (
    from = fromCity,
    to = toCity,
    d = date,
    a = adults,
    c = children,
    ret = tripType === "round_trip" ? returnDate : null
  ) => {
    const isValid = from && to && d;
    const needsReturn = tripType === "round_trip";
    const hasReturn = ret && dayjs(ret).isValid();
  
    setFromError(!from);
    setToError(!to);
    setDateError(!d || (needsReturn && !hasReturn));
  
    if (!isValid || (needsReturn && !hasReturn)) return;

    const dateStr = d.format("YYYY-MM-DD");
    const params = new URLSearchParams({
      from,
      to,
      date: dateStr,
      adults: String(a),
      children: String(c),
    });
    if (hasReturn) {
      params.set("return", dayjs(ret).format("YYYY-MM-DD"));
    }
    navigate(`?${params.toString()}`);
  
    setLoadingTrips(true);
    const fetchPartners = partnerSalesEnabled && !hasReturn;
    setLoadingPartners?.(fetchPartners);
    setShowResults(true);
    setTrips({ direct: [], transfers: [], partners: [] });
    window.scrollTo({ top: 0, behavior: "smooth" });
    try {
      const localPromise = searchTrips(from, to, dateStr, a, c, lang);
      const partnerPromise = fetchPartners
        ? searchPartnerTrips(from, to, dateStr, a, c)
        : Promise.resolve([]);

      const local = await localPromise;
      setTrips({
        direct: local?.direct ?? [],
        transfers: local?.transfers ?? [],
        partners: [],
      });
      setSearchedFromCity(from);
      setSearchedToCity(to);
      setSearchedDate(d);
      setSearchedAdults(a);
      setSearchedChildren(c);
      setSearchedReturnDate?.(hasReturn ? dayjs(ret) : null);
      setLoadingTrips(false);

      try {
        const partners = await partnerPromise;
        setTrips((prev) => ({
          direct: local?.direct ?? prev?.direct ?? [],
          transfers: local?.transfers ?? prev?.transfers ?? [],
          partners: fetchPartners ? (partners ?? []) : [],
        }));
      } finally {
        setLoadingPartners?.(false);
      }
    } catch (error) {
      setTrips({ direct: [], transfers: [], partners: [] });
      setLoadingTrips(false);
      setLoadingPartners?.(false);
    }
  };

  const handleTripTypeChange = (event) => {
    const next = event.target.value;
    setTripType(next);
    if (next === "one_way") {
      setReturnDate(null);
      setDateError(false);
    } else if (!returnDate) {
      const base = date && dayjs(date).isValid() ? dayjs(date) : dayjs();
      setReturnDate(base.add(1, "day"));
    }
  };

  const swapCities = () => {
    setFromCity(toCity);
    setToCity(fromCity);
  };

  const isMobile = useMediaQuery("(max-width:600px)");

  return (
      <Container 
        sx={{
          width: boxWidth || '90%',
          margin: "0 auto",
          backgroundColor: "white",
          borderRadius: "10px",
          border: "1px solid #C4C4C4",
          boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
          position: "relative",
          top: { xs: "-16px", sm: "-60px" },
          mb: { xs: "-16px", sm: "-60px" },
        }}
        >
        <Grid container spacing={1} sx={{
          pt: 2,
          pb: 2
        }}>
        <Grid item xs={12} sx={{ px: { xs: 0.5, sm: 0 } }}>
          <RadioGroup
            row
            value={tripType}
            onChange={handleTripTypeChange}
            sx={{
              gap: { xs: 1, sm: 3 },
              flexWrap: "wrap",
              ml: 0.5,
            }}
          >
            <FormControlLabel
              value="one_way"
              control={
                <Radio
                  size="small"
                  sx={{
                    color: "rgba(0,0,0,0.35)",
                    "&.Mui-checked": { color: BRAND.primary },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: tripType === "one_way" ? 600 : 500,
                    color: tripType === "one_way" ? "text.primary" : "text.secondary",
                  }}
                >
                  {t("search.oneWay")}
                </Typography>
              }
              sx={{ mr: 0 }}
            />
            <FormControlLabel
              value="round_trip"
              control={
                <Radio
                  size="small"
                  sx={{
                    color: "rgba(0,0,0,0.35)",
                    "&.Mui-checked": { color: BRAND.primary },
                  }}
                />
              }
              label={
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: tripType === "round_trip" ? 600 : 500,
                    color:
                      tripType === "round_trip" ? "text.primary" : "text.secondary",
                  }}
                >
                  {t("search.roundTrip")}
                </Typography>
              }
              sx={{ mr: 0 }}
            />
          </RadioGroup>
        </Grid>
        <Grid item xs={12} sx={{ pt: 0.5 }}>
          <Typography
            component="p"
            sx={{
              m: 0,
              ml: 0.5,
              fontSize: "0.95rem",
              fontWeight: 600,
              color: "text.primary",
            }}
          >
            {t("search.chooseRouteAndDate")}
          </Typography>
        </Grid>
            <Grid item xs={12} sm={12} md sx={{ minWidth: 0 }}>
      {/* {showHeadText && (
        <Box> 
          <Box sx={{ textAlign: 'center', width: '100%', pt: 2}}>               
              <Typography sx={{ fontSize: "18px", color: "#666" }}>
                Шукайте квитки на улюблені маршрути та замовляйте їх онлайн швидко та безпечно
              </Typography>
          </Box>                
        </Box>
        )} */}
        <Box sx={{
          display: "flex", flexDirection: "column",
        }}> 
        {/* Поле "Звідки – Куди" */}
        {isMobile ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              border: fromError || toError ? "1px solid #d31717" : '1px solid #C4C4C4',
              borderRadius: "10px",
              overflow: "hidden",
              backgroundColor: "white",
              position: "relative",
            }}
          >
            <Box sx={{  }}>
              <Autocomplete
                forcePopupIcon={false}
                loading={loadingFrom}
                options={fromCityOptions}
                getOptionLabel={(city) => (city && typeof city === "object" && (city.name != null || city.name_en != null) ? getCityDisplay(city, lang) : String(city ?? ""))}
                isOptionEqualToValue={(opt, val) => {
                  if (!val) return false;
                  const keyVal = typeof val === "object" ? getCityKey(val) : val;
                  return getCityKey(opt) === keyVal || getCityDisplay(opt, lang) === val;
                }}
                value={fromCityOptions.find((c) => getCityKey(c) === fromCity || getCityDisplay(c, lang) === fromCity) ?? fromCity}
                inputValue={typeof fromCity === "string" ? fromCity : (fromCity && getCityDisplay(fromCity, lang)) || ""}
                onInputChange={(event, newInputValue) => {
                  setFromCity(newInputValue);
                  fetchCities(newInputValue, setFromCityOptions, setLoadingFrom, topFromCities);
                }}
                onChange={(event, newValue) => {
                  setFromCity(newValue && typeof newValue === "object" ? getCityKey(newValue) : (newValue || ""));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("search.from")}
                    variant="outlined"
                    error={fromError}
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      "data-search-field": "from",
                    }}
                    sx={{
                      "& fieldset": { border: "none" },
                      "& .MuiInputBase-root": {
                        backgroundColor: "white",
                        fontSize: "16px"
                      },
                      "& .MuiInputBase-input": {
                        width: "100% !important",
                        minWidth: "0 !important",
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            </Box>

{/* Divider з кнопкою Swap */}
<Box
    sx={{
      display: "flex",
      justifyContent: "left",
      alignItems: "left",
      px: 1,
      position: "relative",
      height: { xs: "1px", sm: "auto" },
      width: { xs: "90%", sm: "1px" },
      backgroundColor: "#E0E0E0",
    }}
  >
    <IconButton
      onClick={swapCities}
      sx={{
        position: "absolute",
        top: { xs: "-12px", sm: "50%" },
        left: { xs: "calc(100% - 3px)", sm: "-18px" },
        transform: { xs: "rotate(90deg)", sm: "rotate(0deg) translateY(-50%)" },
        color: "#4A90E2",
        backgroundColor: "white",
        zIndex: 1,
        width: 30,
        height: 30,
        "&:hover": { backgroundColor: "#f5f5f5" },
      }}
    >
      <SwapHoriz fontSize="small" />
    </IconButton>
  </Box>
            <Box sx={{ }}>
              <Autocomplete
                forcePopupIcon={false}
                loading={loadingTo}
                options={toCityOptions}
                getOptionLabel={(city) => (city && typeof city === "object" && (city.name != null || city.name_en != null) ? getCityDisplay(city, lang) : String(city ?? ""))}
                isOptionEqualToValue={(opt, val) => {
                  if (!val) return false;
                  const keyVal = typeof val === "object" ? getCityKey(val) : val;
                  return getCityKey(opt) === keyVal || getCityDisplay(opt, lang) === val;
                }}
                value={toCityOptions.find((c) => getCityKey(c) === toCity || getCityDisplay(c, lang) === toCity) ?? toCity}
                inputValue={typeof toCity === "string" ? toCity : (toCity && getCityDisplay(toCity, lang)) || ""}
                onInputChange={(event, newInputValue) => {
                  setToCity(newInputValue);
                  fetchCities(newInputValue, setToCityOptions, setLoadingTo, topToCities);
                }}
                onChange={(event, newValue) => {
                  setToCity(newValue && typeof newValue === "object" ? getCityKey(newValue) : (newValue || ""));
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t("search.to")}
                    variant="outlined"
                    error={toError}
                    fullWidth
                    inputProps={{
                      ...params.inputProps,
                      "data-search-field": "to",
                    }}
                    sx={{
                      "& fieldset": { border: "none" },
                      "& .MuiInputBase-root": {
                        backgroundColor: "white",
                        fontSize: "16px"
                      },
                      "& .MuiInputBase-input": {
                        width: "100% !important",
                        minWidth: "0 !important",
                      },
                    }}
                    InputProps={{
                      ...params.InputProps,
                    }}
                  />
                )}
              />
            </Box>
          </Box>
        ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            borderRadius: "10px",
            border: fromError || toError ? "1px solid #d31717" : "1px solid #C4C4C4",
            backgroundColor: "white",
            overflow: "hidden",
            width: "100%",
            color: "#4A90E2",
            flex: 1,
            position: "relative"
          }}
        >

          <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <Autocomplete
              forcePopupIcon={false}
              options={fromCityOptions}
              getOptionLabel={(city) => (city && typeof city === "object" && (city.name != null || city.name_en != null) ? getCityDisplay(city, lang) : String(city ?? ""))}
              isOptionEqualToValue={(opt, val) => {
                if (!val) return false;
                const keyVal = typeof val === "object" ? getCityKey(val) : val;
                return getCityKey(opt) === keyVal || getCityDisplay(opt, lang) === val;
              }}
              value={fromCityOptions.find((c) => getCityKey(c) === fromCity || getCityDisplay(c, lang) === fromCity) ?? fromCity}
              inputValue={typeof fromCity === "string" ? fromCity : (fromCity && getCityDisplay(fromCity, lang)) || ""}
              onInputChange={(event, newInputValue) => {
                setFromCity(newInputValue);
                fetchCities(newInputValue, setFromCityOptions, setLoadingFrom, topFromCities);
              }}
              onChange={(event, newValue) => {
                setFromCity(newValue && typeof newValue === "object" ? getCityKey(newValue) : (newValue || ""));
              }}
              sx={{
                "& fieldset": { border: "none" },
                "& .MuiInputBase-root": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                  height: "56px",
                  width: "100%",
                },
              }}
              loading={loadingFrom}
              noOptionsText={fromCity ? t("search.noCities") : t("search.enterCity")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("search.from")}
                  variant="outlined"
                  error={fromError}
                  fullWidth
                  inputProps={{
                    ...params.inputProps,
                    "data-search-field": "from",
                  }}
                  sx={{
                    "& fieldset": { border: "none" },
                    "& .MuiInputBase-root": {
                      backgroundColor: "white",
                      borderRadius: "10px",
                      height: "56px",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: cityFieldFontSize(fromCity),
                      width: "100% !important",
                      minWidth: "0 !important",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
            />
            
          </Box>

          <IconButton onClick={swapCities} sx={{ color: "#4A90E2", mx: 0.5, flexShrink: 0 }}>
            <SwapHoriz />
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0, position: 'relative' }}>
            <Autocomplete
              forcePopupIcon={false}
              options={toCityOptions}
              getOptionLabel={(city) => (city && typeof city === "object" && (city.name != null || city.name_en != null) ? getCityDisplay(city, lang) : String(city ?? ""))}
              isOptionEqualToValue={(opt, val) => {
                if (!val) return false;
                const keyVal = typeof val === "object" ? getCityKey(val) : val;
                return getCityKey(opt) === keyVal || getCityDisplay(opt, lang) === val;
              }}
              value={toCityOptions.find((c) => getCityKey(c) === toCity || getCityDisplay(c, lang) === toCity) ?? toCity}
              inputValue={typeof toCity === "string" ? toCity : (toCity && getCityDisplay(toCity, lang)) || ""}
              onInputChange={(event, newInputValue) => {
                setToCity(newInputValue);
                fetchCities(newInputValue, setToCityOptions, setLoadingTo, topToCities);
              }}
              onChange={(event, newValue) => {
                setToCity(newValue && typeof newValue === "object" ? getCityKey(newValue) : (newValue || ""));
              }}
              sx={{
                "& fieldset": { border: "none" },
                "& .MuiInputBase-root": {
                  backgroundColor: "white",
                  borderRadius: "10px",
                  height: "56px",
                  width: "100%",
                },
              }}
              loading={loadingTo}
              noOptionsText={toCity ? t("search.noCities") : t("search.enterCity")}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={t("search.to")}
                  variant="outlined"
                  error={toError}
                  fullWidth
                  inputProps={{
                    ...params.inputProps,
                    "data-search-field": "to",
                  }}
                  sx={{
                    "& fieldset": { border: "none" },
                    "& .MuiInputBase-root": {
                      backgroundColor: "white",
                      borderRadius: "10px",
                      height: "56px",
                    },
                    "& .MuiInputBase-input": {
                      fontSize: cityFieldFontSize(toCity),
                      width: "100% !important",
                      minWidth: "0 !important",
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                  }}
                />
              )}
            />
          </Box>
          </Box>
)}

              {/* Повідомлення про помилку */}
    {(fromError || toError) && (
      <Box
        sx={{
          display: "flex",
          mt: 1,
          pl: 1,
          color: "#d31717",
        }}
      >
        <ErrorOutline sx={{ fontSize: 18, mr: 1 }} />
        <Typography
          variant="body2"
          sx={{ fontWeight: 600 }}
        >
          {t("searchBar.routeRequired")}
        </Typography>
      </Box>
    )}
          </Box>


        </Grid>
        <Grid
          item
          xs={12}
          sm={tripType === "round_trip" ? 12 : true}
          md="auto"
          sx={{
            display: "flex",
            flexShrink: 0,
            width: { md: tripType === "round_trip" ? 372 : 176 },
            minWidth: { md: tripType === "round_trip" ? 372 : 168 },
            maxWidth: { md: "none" },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "stretch",
              width: "100%",
              border: dateError ? "1px solid #d31717" : "1px solid #C4C4C4",
              borderRadius: "10px",
              backgroundColor: "white",
              overflow: "hidden",
              minHeight: 56,
            }}
          >
            <Box sx={{ flex: "1 1 0", minWidth: tripType === "round_trip" ? 160 : 0, px: 1, py: 0.5 }}>
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  fontWeight: 600,
                  lineHeight: 1.2,
                  mb: 0.15,
                }}
              >
                {tripType === "round_trip"
                  ? t("search.dateOutbound")
                  : t("search.date")}
              </Typography>
              <DatePicker
                value={date}
                onChange={(newDate) => {
                  setDate(newDate);
                  if (returnDate && newDate && returnDate.isBefore(newDate, "day")) {
                    setReturnDate(newDate);
                  }
                }}
                format="DD.MM.YYYY"
                adapterLocale="ukUA"
                minDate={dayjs()}
                sx={{
                  width: "auto",
                  "& .MuiFormControl-root, & .MuiInputBase-root": { width: "auto" },
                }}
                slotProps={{
                  textField: {
                    variant: "standard",
                    error: dateError,
                    placeholder: t("search.date"),
                    InputProps: {
                      disableUnderline: true,
                      readOnly: true,
                      sx: {
                        fontSize: "0.95rem",
                        fontWeight: 600,
                        color: "text.primary",
                        alignItems: "center",
                        "& input": {
                          py: 0,
                          width: "7.4em",
                          cursor: "pointer",
                          textOverflow: "clip",
                        },
                      },
                    },
                  },
                  openPickerButton: {
                    sx: { p: 0.25, ml: 0.25, color: "primary.main" },
                  },
                }}
              />
            </Box>
            {tripType === "round_trip" && (
              <>
                <Box
                  sx={{
                    width: "1px",
                    alignSelf: "stretch",
                    bgcolor: "divider",
                    my: 1,
                  }}
                />
                <Box sx={{ flex: "1 1 0", minWidth: 160, px: 1, py: 0.5 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      color: "text.secondary",
                      fontWeight: 600,
                      lineHeight: 1.2,
                      mb: 0.15,
                    }}
                  >
                    {t("search.dateReturn")}
                  </Typography>
                  <DatePicker
                    value={returnDate}
                    onChange={(newDate) => setReturnDate(newDate)}
                    format="DD.MM.YYYY"
                    adapterLocale="ukUA"
                    minDate={date || dayjs()}
                    sx={{
                      width: "auto",
                      "& .MuiFormControl-root, & .MuiInputBase-root": { width: "auto" },
                    }}
                    slotProps={{
                      textField: {
                        variant: "standard",
                        placeholder: "—",
                        InputProps: {
                          disableUnderline: true,
                          readOnly: true,
                          sx: {
                            fontSize: "0.95rem",
                            fontWeight: returnDate ? 600 : 400,
                            color: returnDate ? "text.primary" : "text.secondary",
                            alignItems: "center",
                            "& input": {
                              py: 0,
                              width: "7.4em",
                              cursor: "pointer",
                              textOverflow: "clip",
                            },
                          },
                        },
                      },
                      openPickerButton: {
                        sx: { p: 0.25, ml: 0.25, color: "primary.main" },
                      },
                    }}
                  />
                </Box>
              </>
            )}
          </Box>
        </Grid>

        <Grid item xs={12} sm="auto" md="auto"
          sx={{
            display: "flex",
            flexShrink: 0,
          }}>
        {/* Поле "Кількість пасажирів" */}
        <Button
          variant="outlined"
          sx={{
            textTransform: "none",
            justifyContent: "flex-start",
            border: "1px solid #C4C4C4",
            backgroundColor: "white",
            borderRadius: "10px",
            height: "56px",
            width: { xs: "100%", sm: "auto" },
            minWidth: 0,
            px: 1.25,
            whiteSpace: "nowrap",
            flexShrink: 0,
            color: "#4A90E2",
          }}
          onClick={(event) => setPassengerAnchor(event.currentTarget)}
          startIcon={<People />}
        >
          {adults} {t("search.adultsShort")}, {children} {t("search.childrenShort")}
        </Button>

        <Popover
          open={Boolean(passengerAnchor)}
          anchorEl={passengerAnchor}
          onClose={() => setPassengerAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        >
          <Box sx={{ p: 2, minWidth: 250 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{t("search.adults")}</Typography>
              <Box>
                <IconButton
                  onClick={() => setAdults(Math.max(1, adults - 1))}
                  disabled={adults <= 1}
                >
                  <Remove />
                </IconButton>
                <Typography component="span" sx={{ mx: 1 }}>{adults}</Typography>
                <IconButton
                  onClick={() => setAdults(adults + 1)}
                  disabled={adults + children >= MAX_PASSENGERS}
                >
                  <Add />
                </IconButton>
              </Box>
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography>{t("search.children")}</Typography>
              <Box>
                <IconButton
                  onClick={() => setChildren(Math.max(0, children - 1))}
                  disabled={children <= 0}
                >
                  <Remove />
                </IconButton>
                <Typography component="span" sx={{ mx: 1 }}>{children}</Typography>
                <IconButton
                  onClick={() => setChildren(children + 1)}
                  disabled={adults + children >= MAX_PASSENGERS}
                >
                  <Add />
                </IconButton>
              </Box>
            </Box>
          </Box>
        </Popover>

        </Grid>
        <Grid item xs={12} sm="auto" md="auto"
          sx={{
            display: "flex",
            flexShrink: 0,
          }}>
        {/* Кнопка "Знайти квиток" */}
        <Button
          variant="contained"
          color="primary"
          onClick={() => defaultHandleSearch()}
          sx={{
            height: "56px",
            width: { xs: "100%", sm: "auto" },
            whiteSpace: "nowrap",
            px: 2.5,
            flexShrink: 0,
            borderRadius: "10px",
          }}
          startIcon={<Search />}
          loading={loadingTrips}
        >
          {t("search.findTicket")}
        </Button>
        </Grid>
        </Grid>
      </Container>
  );
};

export default SearchBar;
